import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1715154470884-1c2be0b0129f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-white">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
        <div className="reveal space-y-6 lg:col-span-6">
          <p className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold uppercase tracking-widest text-slate-500">
            <span className="h-2 w-2 rounded-full bg-[#E5007D]" /> Comunicação visual sob medida
          </p>
          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
            Imprima do seu <span className="text-[#0099DD]">tamanho</span>.<br />Saiba o preço <span className="text-[#E5007D]">na hora</span>.
          </h1>
          <p className="max-w-lg text-base text-slate-600 md:text-lg">
            Informe altura e largura, veja o valor calculado pelo m² e finalize a compra em minutos. Acompanhe cada etapa: pagamento, produção e retirada.
          </p>
          <a href="#produtos" className="inline-flex h-12 items-center rounded-full bg-slate-900 px-7 font-bold text-white transition-colors hover:bg-[#0099DD]" data-testid="hero-cta">
            Ver produtos
          </a>
        </div>
        <div className="reveal relative lg:col-span-6" style={{ animationDelay: "150ms" }}>
          <div className="absolute -left-4 -top-4 h-full w-full rounded-md border-2 border-[#EAB308]" />
          <img src={HERO} alt="Impressão gráfica" className="relative aspect-[4/3] w-full rounded-md object-cover shadow-2xl" />
          <div className="absolute -bottom-6 right-6 rounded-md bg-slate-900 px-5 py-4 text-white shadow-xl">
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Ex.: 200 × 100 cm</p>
            <p className="font-mono text-xl font-bold text-[#38bdf8]">= 2,00 m²</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { q: q || undefined, category: category || undefined } })
        .then((r) => setProducts(r.data))
        .catch(() => setProducts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category]);

  const chip = (value, label) => (
    <button key={label} onClick={() => setCategory(value)} data-testid={`category-${value || "todos"}`}
      className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${category === value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-sky-500 hover:text-sky-600"}`}>
      {label}
    </button>
  );

  return (
    <>
      <Hero />
      <section id="produtos" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Nossos produtos</h2>
            <p className="text-sm text-slate-500 md:text-base">Escolha um produto e calcule o valor pelo tamanho.</p>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto..." className="h-11 bg-white pl-9" data-testid="product-search-input" />
          </div>
        </div>
        <div className="mb-8 flex gap-2 overflow-x-auto pb-2">
          {chip("", "Todos")}
          {categories.map((c) => chip(c, c))}
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="product-grid">
          {products === null
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-md" />)
            : products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        {products?.length === 0 && <p className="py-16 text-center text-slate-500" data-testid="no-products">Nenhum produto encontrado.</p>}
      </section>
    </>
  );
}
